
import { Article } from "@/hooks/useMediaMonitoring";
import { FinancialData } from "@/hooks/useFinancialReporting";
import { Review } from "@/lib/types";

// The 5 risk levels used throughout the application
export type RiskLevel = 'Super Safe' | 'Low Risk' | 'Moderate Risk' | 'High Risk' | 'Super High Risk';

// Risk score boundaries (0-100 scale)
const RISK_SCORE_THRESHOLDS = {
  SUPER_SAFE: 20,    // 0-20
  LOW_RISK: 40,      // 21-40
  MODERATE_RISK: 60, // 41-60
  HIGH_RISK: 80,     // 61-80
  // Super High Risk: 81-100
};

interface RiskAssessmentData {
  // Required
  companyName: string;
  reviewCount: number;
  
  // Optional data sources - if not provided, they won't be factored in
  employeeReviews?: Review[];
  mediaArticles?: Article[];
  financialData?: FinancialData[];
  // Would add recruiterReviews, redundancyStats, etc. when those interfaces are created
}

/**
 * Calculates the overall redundancy risk rating for a company
 * based on multiple data sources with equal weighting
 */
export function calculateRiskRating(data: RiskAssessmentData): RiskLevel {
  const { companyName, reviewCount, employeeReviews, mediaArticles, financialData } = data;
  
  // Handle Canva as a special case (based on existing implementation)
  if (companyName === 'Canva') {
    return 'Low Risk';
  }
  
  // Initialize scores array - we'll collect all individual scores here
  const scores: number[] = [];
  
  // 1. Basic review count score
  const reviewCountScore = calculateReviewCountScore(reviewCount);
  scores.push(reviewCountScore);
  
  // 2. Employee Reviews analysis - if available
  if (employeeReviews && employeeReviews.length > 0) {
    const employeeReviewScore = calculateEmployeeReviewScore(employeeReviews);
    scores.push(employeeReviewScore);
  }
  
  // 3. Media Articles analysis - if available
  if (mediaArticles && mediaArticles.length > 0) {
    const mediaScore = calculateMediaScore(mediaArticles);
    scores.push(mediaScore);
  }
  
  // 4. Financial Data analysis - if available
  if (financialData && financialData.length > 0) {
    const financialScore = calculateFinancialScore(financialData);
    scores.push(financialScore);
  }
  
  // Calculate the average score (equal weighting for all sources)
  const avgScore = scores.length > 0 
    ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
    : 50; // Default to moderate risk if no data
  
  // Map the average score to a risk level
  return mapScoreToRiskLevel(avgScore);
}

/**
 * Calculate score based on number of reviews (0-100)
 * More reviews generally provide more confidence in assessment
 */
function calculateReviewCountScore(reviewCount: number): number {
  if (reviewCount === 0) return 50; // moderate risk when no reviews
  if (reviewCount < 3) return 45; // slightly below moderate risk for few reviews
  if (reviewCount < 10) return 40; // low-to-moderate risk for some reviews
  return 30; // lower risk for many reviews (more data generally means more stability)
}

/**
 * Calculate score based on employee reviews (0-100)
 * High score = high risk, low score = low risk
 */
function calculateEmployeeReviewScore(reviews: Review[]): number {
  // Calculate the percentage of negative experiences
  const negativeReviews = reviews.filter(review => review.positiveExperience === false);
  const negativePercentage = (negativeReviews.length / reviews.length) * 100;
  
  // Check if fair work was involved
  const fairWorkInvolvedCount = reviews.filter(review => review.fairWorkInvolved === true).length;
  const fairWorkPercentage = (fairWorkInvolvedCount / reviews.length) * 100;
  
  // Calculate employee review score
  // Higher percentage of negative reviews and fair work involvement increases risk
  return (negativePercentage * 0.7) + (fairWorkPercentage * 0.3);
}

/**
 * Calculate score based on media articles (0-100)
 * High score = high risk, low score = low risk
 */
function calculateMediaScore(articles: Article[]): number {
  const negativeArticles = articles.filter(article => article.sentiment === 'negative');
  const neutralArticles = articles.filter(article => article.sentiment === 'neutral');
  const positiveArticles = articles.filter(article => article.sentiment === 'positive');
  
  // Weight negative articles more heavily than positive ones
  const weightedScore = 
    (negativeArticles.length * 100 + 
     neutralArticles.length * 50 + 
     positiveArticles.length * 0) / articles.length;
  
  return weightedScore;
}

/**
 * Calculate score based on financial data (0-100)
 * High score = high risk, low score = low risk
 */
function calculateFinancialScore(financialData: FinancialData[]): number {
  const negativeFinancials = financialData.filter(data => data.impact === 'negative');
  const neutralFinancials = financialData.filter(data => data.impact === 'neutral');
  const positiveFinancials = financialData.filter(data => data.impact === 'positive');
  
  // Weight negative financial indicators more heavily
  const weightedScore = 
    (negativeFinancials.length * 100 + 
     neutralFinancials.length * 50 + 
     positiveFinancials.length * 0) / financialData.length;
  
  return weightedScore;
}

/**
 * Map a numeric score (0-100) to a risk level
 * Higher score = higher risk
 */
function mapScoreToRiskLevel(score: number): RiskLevel {
  if (score <= RISK_SCORE_THRESHOLDS.SUPER_SAFE) return 'Super Safe';
  if (score <= RISK_SCORE_THRESHOLDS.LOW_RISK) return 'Low Risk';
  if (score <= RISK_SCORE_THRESHOLDS.MODERATE_RISK) return 'Moderate Risk';
  if (score <= RISK_SCORE_THRESHOLDS.HIGH_RISK) return 'High Risk';
  return 'Super High Risk';
}
